import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/InvoicesPage.css";
import { invoicesService, Invoice, InvoiceUpdateData } from "../services/invoices.service";
import { providersService, Provider } from "../services/providers.service";
import { useSelectedColoc } from "../hooks/useSelectedColoc";
import { formatDate, formatCurrency, formatFileName } from "../utils/format";
import { openFileInNewTab } from "../utils/file";
import AddInvoiceForm from "../components/AddInvoiceForm";
import { getErrorMessage } from '../utils/error';

type SortField = "providerName" | "invoiceDate" | "initialAmount" | "invoiceAmount" | "fileName";

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);

  // Providers for filtering
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string>(() => {
    const savedProvider = localStorage.getItem("selectedProviderId");
    return savedProvider || "all";
  });
  const [loadingProviders, setLoadingProviders] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Year selector for coverage timeline
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const prevFilterRef = useRef({ selectedProviderId: "all", selectedYear: new Date().getFullYear() });

  // Sorting
  const [sortField, setSortField] = useState<SortField>("invoiceDate");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedColoc) {
        setInvoices([]);
        setProviders([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setLoadingProviders(true);

        try {
          const providersData = await providersService.getProviders();
          setProviders(providersData);
        } catch {
          // silent
        } finally {
          setLoadingProviders(false);
        }

        const data = await invoicesService.getInvoicesByColocId(
          selectedColoc.id,
          sortField,
          sortDirection
        );
        setInvoices(data);
        setLoading(false);
      } catch (err) {
        setError("Échec du chargement des factures");
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedColoc, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortDirection("DESC");
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    const yearStart = new Date(selectedYear, 0, 1);
    const yearEnd = new Date(selectedYear, 11, 31, 23, 59, 59);

    const isInYear = (invoice: Invoice): boolean => {
      // Invoice date in selected year
      const invDate = new Date(invoice.invoiceDate);
      if (invDate >= yearStart && invDate <= yearEnd) return true;
      // Period overlaps selected year
      if (invoice.periodStartDate && invoice.periodEndDate) {
        const pStart = new Date(invoice.periodStartDate);
        const pEnd = new Date(invoice.periodEndDate);
        if (pEnd >= yearStart && pStart <= yearEnd) return true;
      }
      return false;
    };

    let filtered = invoices.filter(isInYear);

    if (selectedProviderId !== "all") {
      filtered = filtered.filter((invoice) => invoice.providerId === selectedProviderId);

      if (filtered.length === 0 && providers.length > 0 && !loadingProviders) {
        const providerExists = providers.some((provider) => provider.id === selectedProviderId);
        if (!providerExists) {
          setSelectedProviderId("all");
          localStorage.setItem("selectedProviderId", "all");
          setFilteredInvoices(invoices.filter(isInYear));
          return;
        }
      }
    }

    setFilteredInvoices(filtered);

    // Only reset page when filter criteria change, not on data refresh
    const prev = prevFilterRef.current;
    if (prev.selectedProviderId !== selectedProviderId || prev.selectedYear !== selectedYear) {
      setCurrentPage(1);
      prevFilterRef.current = { selectedProviderId, selectedYear };
    }
  }, [selectedProviderId, invoices, providers, loadingProviders, selectedYear]);

  const handleProviderFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const providerId = e.target.value;
    setSelectedProviderId(providerId);
    localStorage.setItem("selectedProviderId", providerId);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentInvoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentInvoices.map((inv) => inv.id)));
    }
  };

  const handleBulkValidate = async () => {
    if (selectedIds.size === 0) return;
    const unvalidated = currentInvoices.filter(
      (inv) => selectedIds.has(inv.id) && !inv.isValidated
    );
    if (unvalidated.length === 0) return;

    setBulkLoading(true);
    try {
      await Promise.all(
        unvalidated.map((inv) =>
          invoicesService.updateInvoice(inv.id, { isValidated: true })
        )
      );
      setSelectedIds(new Set());
      if (selectedColoc) {
        const data = await invoicesService.getInvoicesByColocId(
          selectedColoc.id, sortField, sortDirection
        );
        setInvoices(data);
      }
    } catch (err) {
      setError("Échec de la validation groupée");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.size} facture(s) ?`)) return;

    setBulkLoading(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => invoicesService.deleteInvoice(id))
      );
      setSelectedIds(new Set());
      if (selectedColoc) {
        const data = await invoicesService.getInvoicesByColocId(
          selectedColoc.id, sortField, sortDirection
        );
        setInvoices(data);
      }
    } catch (err) {
      setError("Échec de la suppression groupée");
    } finally {
      setBulkLoading(false);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleEditInvoice = (id: string) => {
    navigate(`/invoices/edit/${id}`);
  };

  const handleValidateInvoice = async (invoice: Invoice, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!invoice.id) return;

    try {
      const updateData: InvoiceUpdateData = {
        isValidated: !invoice.isValidated,
      };

      await invoicesService.updateInvoice(invoice.id, updateData);

      if (selectedColoc) {
        invoicesService
          .getInvoicesByColocId(selectedColoc.id, sortField, sortDirection)
          .then((data) => setInvoices(data))
          .catch(() => setError("Échec du rechargement des factures"));
      }
    } catch (err) {
      setError(getErrorMessage(err, "Échec de la validation de la facture"));
    }
  };

  const handleOpenFile = (invoice: Invoice, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!invoice.fileName || !invoice.colocId) return;
    openFileInNewTab(invoice.colocId, invoice.fileName);
  };

  // Check if filtered invoices have period data
  const showPeriod = useMemo(() => {
    return filteredInvoices.some((inv) => inv.periodStartDate && inv.periodEndDate);
  }, [filteredInvoices]);

  // Build coverage timeline for selected provider + year
  const coverageData = useMemo(() => {
    if (selectedProviderId === "all" || !showPeriod) return null;

    const providerInvoices = invoices.filter(
      (inv) => inv.providerId === selectedProviderId && inv.periodStartDate && inv.periodEndDate && inv.isValidated
    );

    const toUTCDay = (dateStr: string): number => {
      const d = new Date(dateStr);
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const yearStart = Date.UTC(selectedYear, 0, 1);
    const yearEnd = Date.UTC(selectedYear, 11, 31);
    const DAY = 86400000;
    const totalDays = Math.round((yearEnd - yearStart) / DAY) + 1;

    // Build covered segments (clamped to year)
    const segments: { start: number; end: number }[] = [];
    for (const inv of providerInvoices) {
      const s = Math.max(toUTCDay(inv.periodStartDate!), yearStart);
      const e = Math.min(toUTCDay(inv.periodEndDate!), yearEnd);
      if (s <= yearEnd && e >= yearStart) {
        segments.push({ start: s, end: e });
      }
    }

    // Detect overlaps before merging (strictly overlapping, not just contiguous)
    // Use raw (unclamped) segments for overlap detection
    const rawSegments = providerInvoices.map((inv) => ({
      start: toUTCDay(inv.periodStartDate!),
      end: toUTCDay(inv.periodEndDate!),
    }));
    rawSegments.sort((a, b) => a.start - b.start);
    const overlaps: { start: string; end: string }[] = [];
    for (let i = 1; i < rawSegments.length; i++) {
      // Deux jours communs au moins : un seul, c'est le jour du relevé, écrit par
      // EDF à la fin d'une facture ET au début de la suivante (« du 22/05 au
      // 22/06 », puis « du 22/06 au 22/07 »). Ni doublon ni argent compté deux
      // fois — l'alerte n'était que du bruit (Julien, 2026-09-29).
      if (Math.min(rawSegments[i].end, rawSegments[i - 1].end) - rawSegments[i].start >= DAY) {
        const overlapStart = rawSegments[i].start;
        const overlapEnd = Math.min(rawSegments[i].end, rawSegments[i - 1].end);
        const fmtD = (ts: number) => {
          const d = new Date(ts);
          return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
        };
        overlaps.push({ start: fmtD(overlapStart), end: fmtD(overlapEnd) });
      }
    }

    // Merge overlapping segments
    segments.sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [];
    for (const seg of segments) {
      if (merged.length > 0 && seg.start <= merged[merged.length - 1].end + DAY) {
        merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, seg.end);
      } else {
        merged.push({ ...seg });
      }
    }

    // Calculate coverage percentage
    let coveredDays = 0;
    for (const seg of merged) {
      coveredDays += Math.round((seg.end - seg.start) / DAY) + 1;
    }

    // Build bar segments (covered + gaps)
    const bars: { startPct: number; widthPct: number; type: "covered" | "gap" }[] = [];
    let cursor = yearStart;
    for (const seg of merged) {
      if (seg.start > cursor) {
        bars.push({
          startPct: ((cursor - yearStart) / DAY / totalDays) * 100,
          widthPct: (((seg.start - cursor) / DAY) / totalDays) * 100,
          type: "gap",
        });
      }
      bars.push({
        startPct: (((seg.start - yearStart) / DAY) / totalDays) * 100,
        widthPct: (((seg.end - seg.start) / DAY + 1) / totalDays) * 100,
        type: "covered",
      });
      cursor = seg.end + DAY;
    }
    if (cursor <= yearEnd) {
      bars.push({
        startPct: (((cursor - yearStart) / DAY) / totalDays) * 100,
        widthPct: (((yearEnd - cursor) / DAY + 1) / totalDays) * 100,
        type: "gap",
      });
    }

    return { bars, coveredDays, totalDays, hasData: segments.length > 0, overlaps };
  }, [invoices, selectedProviderId, selectedYear, showPeriod]);

  const formatPeriodShort = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const dd = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}/${yy}`;
  };

  const isPeriodOutsideYear = (startDate: string | null | undefined, endDate: string | null | undefined): boolean => {
    if (!startDate || !endDate) return false;
    const s = new Date(startDate);
    const e = new Date(endDate);
    const yearStart = new Date(selectedYear, 0, 1);
    const yearEnd = new Date(selectedYear, 11, 31);
    return e < yearStart || s > yearEnd;
  };

  if (loading) {
    return <div className="loading-indicator">Chargement des factures...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!selectedColoc) {
    return <div className="no-coloc-message">Veuillez sélectionner une colocation pour voir les factures.</div>;
  }

  // Handle successful invoice creation
  const handleInvoiceCreated = () => {
    // Close the modal
    setShowAddInvoiceModal(false);
    
    // Refresh the invoice list
    if (selectedColoc) {
      // Reload both invoices and providers
      setLoading(true);
      
      // Get providers
      providersService.getProviders()
        .then(providersData => {
          setProviders(providersData);
        })
        .catch(() => { /* silent */ });
      
      // Get invoices
      invoicesService.getInvoicesByColocId(
        selectedColoc.id,
        sortField,
        sortDirection
      ).then(data => {
        setInvoices(data);
        setLoading(false);
      }).catch(err => {
        setError('Échec du rechargement des factures');
        setLoading(false);
      });
    }
  };

  return (
    <div className="invoices-container">
      <h2>Factures pour {selectedColoc.name}</h2>
      
      <div className="invoices-actions">
        <div className="filter-container">
          <label htmlFor="provider-filter">Fournisseur:</label>
          <select
            id="provider-filter"
            value={selectedProviderId}
            onChange={handleProviderFilterChange}
            disabled={loadingProviders}
            className="provider-filter-select"
          >
            <option value="all">Tous les fournisseurs</option>
            {providers.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.providerName}
              </option>
            ))}
          </select>
          <div className="year-selector">
            <button className="year-nav" onClick={() => setSelectedYear((y) => y - 1)}>‹</button>
            <span className="year-label">{selectedYear}</span>
            <button className="year-nav" onClick={() => setSelectedYear((y) => y + 1)}>›</button>
          </div>
        </div>
        <button
          className="action-button"
          onClick={() => setShowAddInvoiceModal(true)}
        >
          Ajouter une facture
        </button>
      </div>
      
      {/* Add Invoice Modal */}
      {showAddInvoiceModal && (
        <div className="modal-overlay">
          <AddInvoiceForm 
            colocId={selectedColoc.id}
            onSuccess={handleInvoiceCreated}
            onCancel={() => setShowAddInvoiceModal(false)}
          />
        </div>
      )}
      
      {/* Coverage timeline */}
      {coverageData && coverageData.hasData && (
        <div className="coverage-timeline">
          <div className="timeline-header">
            <span className="coverage-pct">
              {Math.round((coverageData.coveredDays / coverageData.totalDays) * 100)}% couvert
              ({coverageData.coveredDays}/{coverageData.totalDays}j)
            </span>
          </div>
          <div className="timeline-bar">
            {coverageData.bars.map((bar, i) => (
              <div
                key={i}
                className={`timeline-segment timeline-${bar.type}`}
                style={{ left: `${bar.startPct}%`, width: `${bar.widthPct}%` }}
              />
            ))}
          </div>
          <div className="timeline-months">
            {["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"].map((m, i) => (
              <span key={i} className="timeline-month">{m}</span>
            ))}
          </div>
        </div>
      )}

      {/* Overlap warnings */}
      {coverageData && coverageData.overlaps.length > 0 && (
        <div className="overlap-warnings">
          {coverageData.overlaps.map((o, i) => (
            <div key={i} className="overlap-warning-item">
              <span className="warning-icon">⚠️</span>
              Chevauchement de périodes : {o.start} → {o.end}
            </div>
          ))}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-action-bar">
          <span className="bulk-count">{selectedIds.size} sélectionnée(s)</span>
          <button
            className="action-button validate-button"
            onClick={handleBulkValidate}
            disabled={bulkLoading}
          >
            Valider
          </button>
          <button
            className="action-button delete-button"
            onClick={handleBulkDelete}
            disabled={bulkLoading}
          >
            Supprimer
          </button>
          <button
            className="bulk-cancel"
            onClick={() => setSelectedIds(new Set())}
          >
            Annuler
          </button>
        </div>
      )}

      <div className="invoices-list">
        <div className="invoices-header">
          <div className="invoice-column col-inv-checkbox">
            <input
              type="checkbox"
              checked={currentInvoices.length > 0 && selectedIds.size === currentInvoices.length}
              onChange={toggleSelectAll}
            />
          </div>
          {selectedProviderId === "all" && (
            <div
              className={`invoice-column col-inv-provider sortable ${sortField === 'providerName' ? 'sorted-' + sortDirection.toLowerCase() : ''}`}
              onClick={() => handleSort('providerName')}
            >
              Fournisseur {sortField === 'providerName' && <span>{sortDirection === 'ASC' ? '▲' : '▼'}</span>}
            </div>
          )}
          <div className="invoice-column col-inv-allocation">
            Charges
          </div>
          {showPeriod && (
            <div className="invoice-column col-inv-period">
              Période
            </div>
          )}
          <div
            className={`invoice-column col-inv-final-amount sortable ${sortField === 'invoiceAmount' ? 'sorted-' + sortDirection.toLowerCase() : ''}`}
            onClick={() => handleSort('invoiceAmount')}
          >
            Montant final {sortField === 'invoiceAmount' && <span>{sortDirection === 'ASC' ? '▲' : '▼'}</span>}
          </div>
          <div
            className={`invoice-column col-inv-date sortable ${sortField === 'invoiceDate' ? 'sorted-' + sortDirection.toLowerCase() : ''}`}
            onClick={() => handleSort('invoiceDate')}
          >
            Date {sortField === 'invoiceDate' && <span>{sortDirection === 'ASC' ? '▲' : '▼'}</span>}
          </div>
          <div
            className={`invoice-column col-inv-initial-amount sortable ${sortField === 'initialAmount' ? 'sorted-' + sortDirection.toLowerCase() : ''}`}
            onClick={() => handleSort('initialAmount')}
          >
            Montant initial {sortField === 'initialAmount' && <span>{sortDirection === 'ASC' ? '▲' : '▼'}</span>}
          </div>
          <div
            className={`invoice-column col-inv-file sortable ${sortField === 'fileName' ? 'sorted-' + sortDirection.toLowerCase() : ''}`}
            onClick={() => handleSort('fileName')}
          >
            Fichier {sortField === 'fileName' && <span>{sortDirection === 'ASC' ? '▲' : '▼'}</span>}
          </div>
          <div className="actions-column"></div>
        </div>
        
        {currentInvoices.length > 0 ? (
          <>
            {currentInvoices.map((invoice) => (
              <div 
                key={invoice.id} 
                className={`invoice-row ${invoice.isValidated ? 'validated' : ''}`}
                onClick={() => handleEditInvoice(invoice.id)}
              >
                <div className="invoice-column col-inv-checkbox" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(invoice.id)}
                    onChange={() => toggleSelect(invoice.id)}
                  />
                </div>
                {selectedProviderId === "all" && (
                  <div className="invoice-column col-inv-provider" title={invoice.provider?.providerName || 'Inconnu'}>
                    {invoice.provider?.providerName || 'Inconnu'}
                  </div>
                )}
                <div className="invoice-column col-inv-allocation">
                  {invoice.chargeAllocation ? (
                    invoice.chargeAllocation.status === "full" ? (
                      <span className="allocation-badge allocation-full" title={`Imputée à 100% — ${invoice.chargeAllocation.years.join(", ")}`}>
                        ✓ {invoice.chargeAllocation.years.join(", ")}
                      </span>
                    ) : invoice.chargeAllocation.status === "partial" ? (
                      <span className="allocation-badge allocation-partial" title={`Imputée à ${Math.round(invoice.chargeAllocation.totalAllocatedRatio * 100)}% — ${invoice.chargeAllocation.years.join(", ")}`}>
                        ◐ {Math.round(invoice.chargeAllocation.totalAllocatedRatio * 100)}% ({invoice.chargeAllocation.years.join(", ")})
                      </span>
                    ) : (
                      <span className="allocation-badge allocation-none" title="Non imputée">
                        —
                      </span>
                    )
                  ) : (
                    <span className="allocation-badge allocation-none">—</span>
                  )}
                </div>
                {showPeriod && (
                  <div
                    className={`invoice-column col-inv-period${isPeriodOutsideYear(invoice.periodStartDate, invoice.periodEndDate) ? ' period-outside-year' : ''}`}
                    title={
                      invoice.periodStartDate && invoice.periodEndDate
                        ? `${formatPeriodShort(invoice.periodStartDate)} → ${formatPeriodShort(invoice.periodEndDate)}`
                        : "-"
                    }
                  >
                    {invoice.consumptionQuantity != null && (
                      // Affichée sous la période, dans la colonne qui existe déjà :
                      // c'est l'écran où l'on valide, et valider sans voir la
                      // consommation revient à accuser réception les yeux fermés.
                      <span
                        className={`conso-facturee${invoice.consumptionEstimated == null ? ' consumption-inconnue' : ''}`}
                        title={
                          invoice.consumptionEstimated == null
                            ? "Nature du relevé non renseignée — masque l'historique de consommation"
                            : invoice.consumptionEstimated
                            ? "Consommation estimée"
                            : "Relevé réel"
                        }
                      >
                        {invoice.consumptionQuantity.toLocaleString('fr-FR')} {invoice.consumptionUnit === 'm3' ? 'm³' : invoice.consumptionUnit}
                        {/* La nature se voit dans la LISTE : c'est là qu'on
                            cherche la facture à corriger quand le graphique
                            annonce une consommation non vérifiable. */}
                        {invoice.consumptionEstimated == null
                          ? ' · nature ?'
                          : invoice.consumptionEstimated
                          ? ' · estimé'
                          : ''}
                      </span>
                    )}
                    {invoice.periodStartDate && invoice.periodEndDate
                      ? <>
                          {formatPeriodShort(invoice.periodStartDate)} → {formatPeriodShort(invoice.periodEndDate)}
                          {isPeriodOutsideYear(invoice.periodStartDate, invoice.periodEndDate) && (
                            <span className="period-icon" title={`Hors ${selectedYear}`}>⚠️</span>
                          )}
                        </>
                      : "-"}
                  </div>
                )}
                <div className="invoice-column col-inv-final-amount" title={formatCurrency(invoice.invoiceAmount)}>
                  {formatCurrency(invoice.invoiceAmount)}
                </div>
                <div className="invoice-column col-inv-date" title={formatDate(invoice.invoiceDate)}>
                  {formatDate(invoice.invoiceDate)}
                </div>
                <div className="invoice-column col-inv-initial-amount" title={formatCurrency(invoice.initialAmount)}>
                  {formatCurrency(invoice.initialAmount)}
                </div>
                <div
                  className="invoice-column col-inv-file file-column"
                  title={invoice.fileName}
                  onClick={(e) => handleOpenFile(invoice, e)}
                >
                  <span className="file-link">{formatFileName(invoice.fileName)}</span>
                </div>
                <div className="actions-column">
                  {!invoice.isValidated && (
                    <button 
                      className="action-button validate-button"
                      onClick={(e) => handleValidateInvoice(invoice, e)}
                      title="Valider la facture"
                    >
                      Valider
                    </button>
                  )}
                  {invoice.isValidated && (
                    <span className="validated-text">Validée</span>
                  )}
                  <button 
                    className="action-button edit-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditInvoice(invoice.id);
                    }}
                    title="Modifier la facture"
                  >
                    Modifier
                  </button>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="no-invoices-message">
            Aucune facture disponible pour le moment. Les factures apparaîtront ici une fois traitées.
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {filteredInvoices.length > itemsPerPage && (
        <div className="pagination">
          {Array.from({ length: Math.ceil(filteredInvoices.length / itemsPerPage) }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              className={`pagination-button ${currentPage === i + 1 ? 'active' : ''}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
